"use client";

import {
  AtSign,
  Check,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  LoaderCircle,
  ScanLine,
  Smartphone,
  X,
} from "lucide-react";
import Image from "next/image";
import { useEffect, useId, useRef, useState } from "react";
import { PhoneAuthForm } from "@/components/auth/phone-auth-form";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  UPI_ANY_APP,
  UPI_APP_PREVIEW_COUNT,
  type UpiAppOption,
} from "../lib/upi-apps";
import { describeIntentOption, describeQrOption } from "../lib/upi-surface";
import {
  getPaymentMethodBadge,
  getPaymentMethodDisplayLabel,
  getPaymentMethodDisplaySubtitle,
  type RazorpayCustomPaymentController,
} from "./use-razorpay-custom-payment";

export type CustomPaymentMethodSheetProps = {
  controller: RazorpayCustomPaymentController<unknown>;
};

/**
 * Removal, per row.
 *
 * The only change a stored method accepts. There is no edit affordance anywhere
 * in this sheet on purpose: a card is a Razorpay token and the token API has no
 * update, and a UPI ID that has paid for something is a fact about a past
 * payment. Getting a different one means adding a different one.
 */
function RemoveMethodButton({
  controller,
  methodId,
  label,
}: {
  controller: RazorpayCustomPaymentController<unknown>;
  methodId: number;
  label: string;
}) {
  const isRemoving = controller.removingMethodId === methodId;
  return (
    <button
      type="button"
      aria-label={`Remove ${label}`}
      disabled={isRemoving}
      onClick={() => {
        void controller.removeMethod(methodId);
      }}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-text-tertiary transition-colors hover:bg-surface hover:text-text-primary disabled:opacity-50 dark:text-white/45 dark:hover:bg-white/[0.1] dark:hover:text-white"
    >
      {isRemoving ? (
        <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <X className="h-4 w-4" aria-hidden="true" />
      )}
    </button>
  );
}

/**
 * One UPI app, as a row in the payment-method list.
 *
 * The mark is a local asset (`public/upi-apps/`, ~1 KB each) rather than the
 * provider's CDN: a payment screen should not depend on a third party to render,
 * and two of Razorpay's own icon URLs answer 403. A missing or failed mark falls
 * back to the app's initial instead of leaving a hole.
 */
function UpiAppRow({
  app,
  selected,
  onSelect,
}: {
  app: UpiAppOption;
  selected: boolean;
  onSelect: (shortcode: string) => void;
}) {
  const [iconFailed, setIconFailed] = useState(false);

  return (
    <button
      type="button"
      onClick={() => onSelect(app.shortcode)}
      className="flex w-full items-center gap-3 px-3 py-3 text-left"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white">
        {app.iconUrl && !iconFailed ? (
          <Image
            src={app.iconUrl}
            alt=""
            width={28}
            height={28}
            className="h-7 w-7 object-contain"
            onError={() => setIconFailed(true)}
            unoptimized
            loading="eager"
          />
        ) : (
          <span className="text-sm font-bold text-slate-700">
            {app.name.charAt(0)}
          </span>
        )}
      </div>
      <span className="min-w-0 flex-1 truncate text-base font-semibold">
        {app.name}
      </span>
      {selected ? (
        <Check
          className="h-4 w-4 shrink-0 text-success dark:text-emerald-300"
          aria-hidden="true"
        />
      ) : (
        <ChevronRight
          className="h-4 w-4 shrink-0 text-text-tertiary dark:text-white/40"
          aria-hidden="true"
        />
      )}
    </button>
  );
}

export function CustomPaymentMethodSheet({
  controller,
}: CustomPaymentMethodSheetProps) {
  /**
   * Is a typed UPI ID a peer option here, or a fallback?
   *
   * A peer when there is no app route to prefer (desktop), when the app route
   * has just failed (`sheetError` is how that failure comes back), or when the
   * payer already keeps UPI IDs — a saved handle is a chosen credential, not a
   * last resort. Otherwise it is a fallback and renders as a link.
   */
  /**
   * Four rows then "VIEW ALL". Fifteen is a scroll, and a scroll inside a
   * payment-method list is the friction this whole flow exists to remove.
   */
  const cardFormRef = useRef<HTMLDivElement>(null);
  const cardErrorId = useId();
  useEffect(() => {
    if (
      controller.sheetOpen &&
      controller.sheetView === "add_card" &&
      controller.sheetError &&
      controller.cardErrorField
    ) {
      cardFormRef.current
        ?.querySelector<HTMLInputElement>('[aria-invalid="true"]')
        ?.focus();
    }
  }, [
    controller.sheetOpen,
    controller.sheetView,
    controller.sheetError,
    controller.cardErrorField,
  ]);
  const [showAllApps, setShowAllApps] = useState(false);
  const allApps = controller.upiApps?.apps ?? [];
  const upiApps = showAllApps
    ? allApps
    : allApps.slice(0, UPI_APP_PREVIEW_COUNT);
  const overflowCount = allApps.length - upiApps.length;

  return (
    <Drawer
      open={controller.sheetOpen}
      onOpenChange={controller.setSheetOpen}
      repositionInputs={false}
    >
      <DrawerContent
        className={`mx-auto max-w-[520px] rounded-t-[24px] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.12] dark:bg-[#111114] dark:text-white ${
          controller.sheetView === "verify_phone"
            ? "max-h-[calc(100dvh-max(env(safe-area-inset-top),12px))]"
            : "max-h-[82dvh]"
        }`}
      >
        <DrawerHeader className="px-4 pb-2 text-left">
          <DrawerTitle className="flex items-center gap-2 text-xl font-semibold">
            {controller.sheetView !== "methods" ? (
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-full text-text-secondary hover:bg-surface-alt hover:text-text-primary dark:text-white/80 dark:hover:bg-glass-bg-08 dark:hover:text-white"
                onClick={() => controller.setSheetView("methods")}
              >
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>
            ) : null}
            {controller.sheetView === "methods"
              ? "Select Payment Method"
              : controller.sheetView === "add_card"
                ? "Add Card"
                : controller.sheetView === "add_upi"
                  ? "Add UPI ID"
                  : "Verify your mobile number"}
          </DrawerTitle>
        </DrawerHeader>

        <div className="overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom)+16px)]">
          {controller.sheetView === "methods" ? (
            <div className="space-y-5">
              {/*
                Above everything, because the commonest reason this sheet reopens
                is that a UPI app failed to open — and that message belongs over
                the whole list of alternatives, not buried under Cards where it
                used to sit reading like a card error.
              */}
              {controller.sheetError ? (
                <div className="rounded-xl border border-error-border bg-error-bg px-3 py-2.5 text-xs text-error dark:border-red-400/25 dark:bg-red-400/[0.08] dark:text-red-100">
                  {controller.sheetError}
                </div>
              ) : null}
              <section className="space-y-2.5">
                <h3 className="text-base font-semibold text-text-tertiary dark:text-white/48">
                  Cards
                </h3>
                {controller.storedCards.map((method) => (
                  <div
                    key={method.id}
                    className="flex w-full items-center gap-3 rounded-[18px] border border-border bg-surface-alt pr-2 dark:border-white/[0.14] dark:bg-glass-bg-04"
                  >
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-center gap-3 px-3 py-3 text-left"
                      onClick={() => controller.selectStoredMethod(method)}
                    >
                      <div className="flex h-10 min-w-12 shrink-0 items-center justify-center rounded-lg bg-white px-2">
                        <span className="text-xs font-bold tracking-[-0.02em] text-[#1f3b8f] uppercase">
                          {getPaymentMethodBadge(method, "card")}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-base font-semibold">
                          {getPaymentMethodDisplayLabel(method)}
                        </div>
                        <div className="truncate text-xs text-text-tertiary dark:text-white/45">
                          {getPaymentMethodDisplaySubtitle(method) ??
                            "Saved card"}
                        </div>
                      </div>
                      <span className="shrink-0 text-xs font-semibold">
                        USE
                      </span>
                    </button>
                    <RemoveMethodButton
                      controller={controller}
                      methodId={method.id}
                      label={getPaymentMethodDisplayLabel(method)}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded-[18px] border border-border bg-surface-alt px-3 py-3 text-left dark:border-white/[0.14] dark:bg-glass-bg-04"
                  onClick={controller.startAddCard}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white">
                    <CreditCard
                      className="h-5 w-5 text-slate-800"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-base font-semibold">
                      Add credit or debit card
                    </div>
                    <div className="text-xs text-text-tertiary dark:text-white/45">
                      Save securely for faster checkout
                    </div>
                  </div>
                  <span className="text-xs font-semibold">ADD</span>
                </button>
              </section>

              {controller.cardOnly ? (
                <p className="px-1 text-xs text-text-tertiary dark:text-white/45">
                  Holds work on cards only: the amount is held, not charged,
                  until the host approves you.
                </p>
              ) : (
                <section className="space-y-2.5">
                  <h3 className="text-base font-semibold text-text-tertiary dark:text-white/48">
                    UPI
                  </h3>
                  <div className="overflow-hidden rounded-[18px] border border-border bg-surface-alt dark:border-white/[0.14] dark:bg-glass-bg-04">
                    {/*
                    The apps are rows in THIS list, not behind another screen.
                    Choosing an app is a method choice — the same kind of choice
                    as picking a saved card — so it belongs beside the cards and
                    the saved UPI IDs. An intermediate "Pay by UPI app" row that
                    opened a second drawer to pick which UPI was a step that
                    bought nothing.

                    Four rows then "VIEW ALL": fifteen is a scroll, and four
                    covers almost all UPI volume.
                  */}
                    {upiApps.length > 0 ||
                    controller.upiApps?.supportsAnyApp ? (
                      <>
                        {/*
                        Only when the native shell's OS confirmed these apps
                        are on the device. On mobile web the list is
                        Razorpay's user-agent guess, and saying "installed"
                        there would claim knowledge no browser has.
                      */}
                        {controller.upiApps?.source === "native_installed" &&
                        upiApps.length > 0 ? (
                          <div className="px-3 pt-2.5 text-xs text-text-tertiary dark:text-white/45">
                            Installed on this phone
                          </div>
                        ) : null}
                        {upiApps.map((app, index) => (
                          <div key={app.shortcode}>
                            {index > 0 ? (
                              <div className="mx-3 border-t border-border dark:border-white/[0.12]" />
                            ) : null}
                            <UpiAppRow
                              app={app}
                              selected={
                                controller.selectedUpiApp === app.shortcode
                              }
                              onSelect={controller.selectUpiApp}
                            />
                          </div>
                        ))}

                        {overflowCount > 0 ? (
                          <>
                            <div className="mx-3 border-t border-border dark:border-white/[0.12]" />
                            <button
                              type="button"
                              onClick={() => setShowAllApps(true)}
                              className="flex w-full items-center justify-between px-3 py-3 text-left"
                            >
                              <span className="text-base font-semibold">
                                UPI Apps
                              </span>
                              <span className="text-xs font-semibold text-brand uppercase dark:text-white">
                                View all
                              </span>
                            </button>
                          </>
                        ) : null}

                        {/*
                        The honest catch-all, Android only. `any` is a bare
                        `upi://pay`, which the OS answers with its own chooser
                        built from what is genuinely installed — so this row
                        cannot be wrong about anything, and it covers every app
                        not listed above.
                      */}
                        {controller.upiApps?.supportsAnyApp ? (
                          <>
                            {upiApps.length > 0 ? (
                              <div className="mx-3 border-t border-border dark:border-white/[0.12]" />
                            ) : null}
                            <UpiAppRow
                              app={{
                                shortcode: UPI_ANY_APP,
                                name: "Other UPI app",
                                iconUrl: null,
                              }}
                              selected={
                                controller.selectedUpiApp === UPI_ANY_APP
                              }
                              onSelect={controller.selectUpiApp}
                            />
                          </>
                        ) : null}
                        <div className="mx-3 border-t border-border dark:border-white/[0.12]" />
                      </>
                    ) : controller.upiAppProbeComplete &&
                      !controller.upiSurface.isDesktop ? (
                      /*
                       * No direct app list — a native binary too old to say what
                       * is installed, one whose OS found no UPI app, or a probe
                       * that could not answer. Razorpay's managed screen owns the
                       * deep-link attempt there, so an ambiguous cancel/no-app
                       * signal cannot remove a method from this list.
                       */
                      <>
                        <button
                          type="button"
                          className="flex w-full items-center gap-3 px-3 py-3 text-left"
                          onClick={controller.selectUpiIntent}
                        >
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white">
                            <Smartphone
                              className="h-5 w-5 text-slate-700"
                              aria-hidden="true"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-base font-semibold">
                              Pay by UPI app
                            </div>
                            <div className="text-xs text-text-tertiary dark:text-white/45">
                              {describeIntentOption(
                                controller.upiSurface,
                                controller.nativePaymentShell,
                              )}
                            </div>
                          </div>
                          {/*
                          No badge on iOS mobile web. This row leads to the
                          provider's own app tiles, and a tile for an app this
                          iPhone lacks ends at Safari's "the address is
                          invalid" — an alert the page cannot see, so it cannot
                          recover from it either. The row stays, because for
                          the payer who has the app it is the fastest route;
                          what it stops doing is out-ranking a card or a typed
                          UPI ID, neither of which can fail that way.
                        */}
                          {controller.providerIntentRecommended ? (
                            <span className="rounded-full bg-success-bg px-2 py-1 text-[10px] font-semibold text-success uppercase dark:bg-emerald-300/12 dark:text-emerald-200">
                              Recommended
                            </span>
                          ) : null}
                        </button>
                        <div className="mx-3 border-t border-border dark:border-white/[0.12]" />
                      </>
                    ) : null}

                    {controller.supportsUpiQr ? (
                      <>
                        <button
                          type="button"
                          className="flex w-full items-center gap-3 px-3 py-3 text-left"
                          onClick={controller.selectUpiQr}
                        >
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white">
                            <ScanLine
                              className="h-5 w-5 text-slate-700"
                              aria-hidden="true"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-base font-semibold">
                              Scan QR code
                            </div>
                            <div className="text-xs text-text-tertiary dark:text-white/45">
                              {describeQrOption(controller.upiSurface)}
                            </div>
                          </div>
                          {controller.upiSurface.isDesktop ? (
                            <span className="rounded-full bg-success-bg px-2 py-1 text-[10px] font-semibold text-success uppercase dark:bg-emerald-300/12 dark:text-emerald-200">
                              Recommended
                            </span>
                          ) : null}
                        </button>
                        <div className="mx-3 border-t border-border dark:border-white/[0.12]" />
                      </>
                    ) : null}

                    {controller.storedUpiMethods.map((method, index) => {
                      // The handle IS the identity of a UPI method, so it leads.
                      // Every row would otherwise just read "UPI".
                      const vpa =
                        method.metadata?.upiVpa ?? method.subtitle ?? "";
                      const holder = method.metadata?.upiHolderName ?? null;
                      const isPending = method.status === "pending";
                      const subtitle =
                        [holder, isPending ? "Not used yet" : null]
                          .filter(Boolean)
                          .join(" • ") || "UPI ID";

                      return (
                        <div key={method.id}>
                          {index > 0 ? (
                            <div className="mx-3 border-t border-border dark:border-white/[0.12]" />
                          ) : null}
                          <div className="flex w-full items-center justify-between gap-2 px-3 py-3">
                            <button
                              type="button"
                              className="flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-none"
                              onClick={() =>
                                controller.selectStoredMethod(method)
                              }
                            >
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white">
                                <AtSign
                                  className="h-5 w-5 text-slate-700"
                                  aria-hidden="true"
                                />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-base font-semibold text-text-primary dark:text-white">
                                  {vpa}
                                </div>
                                <div className="truncate text-xs text-text-tertiary dark:text-white/45">
                                  {subtitle}
                                </div>
                              </div>
                            </button>

                            <div className="flex shrink-0 items-center gap-2">
                              <button
                                type="button"
                                className="text-xs font-semibold text-text-primary hover:underline dark:text-white"
                                onClick={() =>
                                  controller.selectStoredMethod(method)
                                }
                              >
                                USE
                              </button>
                              <RemoveMethodButton
                                controller={controller}
                                methodId={method.id}
                                label={vpa}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {controller.storedUpiMethods.length > 0 ? (
                      <div className="mx-3 border-t border-border dark:border-white/[0.12]" />
                    ) : null}

                    {/*
                    Kept as a full row. Collect is past its NPCI sunset outside
                    the iOS exemption, but it is still the fallback that works
                    when an app handoff will not, and burying it behind a link
                    made the one recovery path hardest to find.
                  */}
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 px-3 py-3 text-left"
                      onClick={controller.startAddUpi}
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white">
                        <AtSign
                          className="h-5 w-5 text-slate-700"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-base font-semibold">
                          Add UPI ID
                        </div>
                        <div className="text-xs text-text-tertiary dark:text-white/45">
                          Pay via UPI Collect
                        </div>
                      </div>
                      <span className="text-xs font-semibold">ADD</span>
                    </button>
                  </div>
                </section>
              )}
            </div>
          ) : null}

          {controller.sheetView === "add_card" ? (
            <div
              ref={cardFormRef}
              className="ph-no-capture space-y-2.5"
              data-ph-mask
            >
              {controller.sheetError ? (
                <div
                  id={cardErrorId}
                  role="alert"
                  className="rounded-xl border border-error-border bg-error-bg px-3 py-2 text-xs text-error dark:border-red-400/25 dark:bg-red-400/[0.08] dark:text-red-100"
                >
                  {controller.sheetError}
                </div>
              ) : null}
              <input
                type="text"
                autoComplete="cc-name"
                aria-label="Name on card"
                aria-invalid={Boolean(
                  controller.sheetError && controller.cardErrorField === "name",
                )}
                aria-describedby={
                  controller.sheetError ? cardErrorId : undefined
                }
                value={controller.cardName}
                onChange={(event) => controller.setCardName(event.target.value)}
                placeholder="Name on card"
                className="h-11 w-full rounded-xl border border-border bg-surface-alt px-3 text-base font-medium text-text-primary outline-none placeholder:text-text-tertiary focus:border-brand md:text-sm dark:border-white/[0.12] dark:bg-glass-bg-06 dark:text-white dark:placeholder:text-white/38 dark:focus:border-white/35"
              />
              <input
                type="text"
                value={controller.cardNickname}
                onChange={(event) =>
                  controller.setCardNickname(event.target.value)
                }
                aria-label="Card nickname (optional)"
                placeholder="Nickname (optional)"
                className="h-11 w-full rounded-xl border border-border bg-surface-alt px-3 text-base font-medium text-text-primary outline-none placeholder:text-text-tertiary focus:border-brand md:text-sm dark:border-white/[0.12] dark:bg-glass-bg-06 dark:text-white dark:placeholder:text-white/38 dark:focus:border-white/35"
              />
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="cc-number"
                aria-label="Card number"
                aria-invalid={Boolean(
                  controller.sheetError &&
                    controller.cardErrorField === "number",
                )}
                aria-describedby={
                  controller.sheetError ? cardErrorId : undefined
                }
                value={controller.cardNumber}
                onChange={(event) =>
                  controller.setCardNumberInput(event.target.value)
                }
                placeholder="Card number"
                className="h-11 w-full rounded-xl border border-border bg-surface-alt px-3 text-base font-medium text-text-primary outline-none placeholder:text-text-tertiary focus:border-brand md:text-sm dark:border-white/[0.12] dark:bg-glass-bg-06 dark:text-white dark:placeholder:text-white/38 dark:focus:border-white/35"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="cc-exp"
                  aria-label="Expiry date, MM/YY"
                  aria-invalid={Boolean(
                    controller.sheetError &&
                      controller.cardErrorField === "expiry",
                  )}
                  aria-describedby={
                    controller.sheetError ? cardErrorId : undefined
                  }
                  value={controller.cardExpiry}
                  onChange={(event) =>
                    controller.setCardExpiryInput(event.target.value)
                  }
                  placeholder="Valid thru MM/YY"
                  className="h-11 min-w-0 rounded-xl border border-border bg-surface-alt px-3 text-base font-medium text-text-primary outline-none placeholder:text-text-tertiary focus:border-brand md:text-sm dark:border-white/[0.12] dark:bg-glass-bg-06 dark:text-white dark:placeholder:text-white/38 dark:focus:border-white/35"
                />
                <input
                  type="password"
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  aria-label="Security code, CVV"
                  aria-invalid={Boolean(
                    controller.sheetError &&
                      controller.cardErrorField === "cvv",
                  )}
                  aria-describedby={
                    controller.sheetError ? cardErrorId : undefined
                  }
                  value={controller.cardCvv}
                  onChange={(event) =>
                    controller.setCardCvvInput(event.target.value)
                  }
                  placeholder="CVV"
                  className="h-11 min-w-0 rounded-xl border border-border bg-surface-alt px-3 text-base font-medium text-text-primary outline-none placeholder:text-text-tertiary focus:border-brand md:text-sm dark:border-white/[0.12] dark:bg-glass-bg-06 dark:text-white dark:placeholder:text-white/38 dark:focus:border-white/35"
                />
              </div>
              <div className="flex items-start gap-2.5 rounded-xl border border-border bg-surface-alt p-2.5 dark:border-white/[0.12] dark:bg-glass-bg-04">
                <Checkbox
                  checked={controller.saveCard}
                  onCheckedChange={(checked) =>
                    controller.setSaveCard(checked === true)
                  }
                  className="mt-0.5 border-border-strong data-[state=checked]:border-brand data-[state=checked]:bg-brand data-[state=checked]:text-brand-foreground dark:border-white/30 dark:data-[state=checked]:border-white dark:data-[state=checked]:bg-white dark:data-[state=checked]:text-black"
                />
                <span className="text-xs text-text-secondary dark:text-white/68">
                  Save this card securely for future payments. You may still
                  need CVV on repeat payments.
                </span>
              </div>
              <Button
                type="button"
                className="h-11 w-full rounded-xl bg-brand font-semibold text-brand-foreground hover:bg-brand-hover dark:bg-white dark:text-black dark:hover:bg-white/90"
                onClick={controller.applyCardDraft}
              >
                Use this card
              </Button>
            </div>
          ) : null}

          {/*
            The verification step is a VIEW of this sheet, not a drawer stacked
            on top of it. That keeps the selected method, the basket and the
            pinned idempotency key alive across it — and avoids two vaul drawers
            fighting over the focus trap and the scroll lock. The mobile-number
            field already lived in the two views below, so this is where it
            belongs.
          */}
          {controller.sheetView === "verify_phone" ? (
            <div className="space-y-4 pb-2">
              <p className="text-sm text-text-secondary dark:text-white/70">
                We'll text you a code. Verifying keeps this number on your
                current Hiive account, enables OTP sign-in, and saves it for
                checkout. It won't create another account or subscribe you to
                promotions. If the number already opens a Hiive account, you'll
                review its profile, bookings, and money before anything is
                combined.
              </p>
              {controller.sheetError ? (
                <div className="rounded-xl border border-error-border bg-error-bg px-3 py-2 text-xs text-error dark:border-red-400/25 dark:bg-red-400/[0.08] dark:text-red-100">
                  {controller.sheetError}
                </div>
              ) : null}
              <PhoneAuthForm
                mode={{ kind: "attach", isReplacement: false }}
                onSuccess={() => {
                  // Back to review. Deliberately does not charge: an OTP proved
                  // a phone number, it did not authorise a payment.
                  void controller.completePhoneVerification();
                }}
                onCancel={() => controller.setSheetView("methods")}
                footer={
                  <p className="text-center text-xs text-text-tertiary dark:text-white/45">
                    Your payment details are still here. You'll come straight
                    back to them.
                  </p>
                }
              />
            </div>
          ) : null}

          {controller.sheetView === "add_upi" ? (
            <div className="space-y-2.5">
              {controller.sheetError ? (
                <div className="rounded-xl border border-error-border bg-error-bg px-3 py-2 text-xs text-error dark:border-red-400/25 dark:bg-red-400/[0.08] dark:text-red-100">
                  {controller.sheetError}
                </div>
              ) : null}
              <input
                type="text"
                inputMode="email"
                autoComplete="off"
                value={controller.upiId}
                onChange={(event) => controller.setUpiId(event.target.value)}
                placeholder="name@bank"
                className="h-11 w-full rounded-xl border border-border bg-surface-alt px-3 text-base font-medium text-text-primary outline-none placeholder:text-text-tertiary focus:border-brand md:text-sm dark:border-white/[0.12] dark:bg-glass-bg-06 dark:text-white dark:placeholder:text-white/38 dark:focus:border-white/35"
              />
              {/*
                The name is optional because NPCI masks it — a real response for
                a valid address was "ZI*********************", which reads as a
                broken string rather than a confirmation. So the confirmation
                stands on its own and the name only joins it when it is one.
              */}
              {controller.upiVerified ? (
                <p className="px-1 text-xs text-success dark:text-emerald-300">
                  {controller.upiHolderName
                    ? `Verified · ${controller.upiHolderName}`
                    : "UPI ID verified"}
                </p>
              ) : null}
              {/*
                No "save this?" checkbox any more. Adding an id IS saving it —
                that is what makes several of them keepable and selectable — and
                the remove button on each row is the way back out. A checkbox
                asking permission to remember an address, next to a list the
                address is about to appear in, was two ideas for one thing.
              */}
              {/*
                No longer promises a check. The provider's VPA validator is not
                available on every account, and when it is not, that sentence
                was telling people their id had been verified with their bank
                moments before the charge was refused for exactly that id.
              */}
              <p className="px-1 text-xs text-text-tertiary dark:text-white/45">
                Saved to your payment methods so you don't have to type it
                again. You can remove it any time.
              </p>
              {/*
                The provider is asked whether the address exists before it is
                accepted. Disabled while that is in flight so the request cannot
                be fired twice, and labelled so the wait is explained rather than
                looking like a dead button.
              */}
              <Button
                type="button"
                disabled={controller.isValidatingUpi}
                className="h-11 w-full rounded-xl bg-brand font-semibold text-brand-foreground hover:bg-brand-hover disabled:opacity-60 dark:bg-white dark:text-black dark:hover:bg-white/90"
                onClick={() => {
                  void controller.applyUpiDraft();
                }}
              >
                {controller.isValidatingUpi ? "Checking…" : "Use this UPI ID"}
              </Button>
            </div>
          ) : null}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
